import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import UserAvatar from '../../userAvatar';
import ConditionalTooltip from '../../conditionalTooltip';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      position: 'relative',
      width: 'fit-content',
    },
    row: {
      backgroundColor: primary.white,
      boxSizing: 'border-box',
      borderBottom: `1px solid ${primary.bluish8}`,
      display: 'grid',
      gridTemplateColumns: '220px 1fr',
      gridColumnGap: 8,
      paddingBottom: spacing(2),
      marginBottom: spacing(2),
      width: 'fit-content',
      '&:last-of-type': {
        marginBottom: 0,
      },
      [breakpoints.up('xl')]: {
        gridTemplateColumns: '298px 1fr',
      },
    },
    user: {
      alignSelf: 'center',
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 16,
      lineHeight: '20px',
    },
    days: {
      display: 'grid',
      gridColumnGap: 2,
      gridTemplateColumns: ({ daysCount }) => `repeat(${daysCount}, 20px)`,
      width: 'fit-content',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns: ({ daysCount }) => `repeat(${daysCount}, 24px)`,
      },
      [breakpoints.up('xl')]: {
        gridTemplateColumns: ({ daysCount }) => `repeat(${daysCount}, 26px)`,
      },
    },
    day: {
      boxSizing: 'border-box',
      backgroundColor: primary.bluish8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 97,
      width: 20,
      height: 20,
      userSelect: 'none',
      [breakpoints.up('xLg')]: {
        width: 24,
        height: 24,
      },
      [breakpoints.up('xl')]: {
        width: 26,
        height: 26,
      },
    },
    weekend: {
      backgroundColor: primary.bluish7,
    },
    holiday: {
      backgroundColor: primary.white,
      border: `1px solid ${primary.bluish7}`,
    },
    holidayTooltip: {
      fontFamily: 'ProximaNova-Bold',
    },
    approvedAbsence: {
      backgroundColor: primary.green17,
    },
    pendingAbsenceDoubleApprove: {
      backgroundColor: primary.white,
      background: `linear-gradient(45deg,
        ${primary.red8} 0 48%,
        ${primary.white} 48% 52%,
        ${primary.red8} 52% 100%)`,
    },
    halfApprovedAbsence: {
      backgroundColor: primary.white,
      background: `linear-gradient(45deg,
        ${primary.green17} 0 48%,
        ${primary.white} 48% 52%,
        ${primary.red8} 52% 100%)`,
    },
    approvedAbsenceDoubleApprove: {
      backgroundColor: primary.white,
      background: `linear-gradient(45deg,
        ${primary.green17} 0 48%,
        ${primary.white} 48% 52%,
        ${primary.green17} 52% 100%)`,
    },
    pendingAbsence: {
      backgroundColor: primary.orange,
    },
    pendingAbsenceActive: {
      cursor: 'pointer',
    },
    dayLabel: {
      fontSize: 8,
      lineHeight: '12px',
      zIndex: 9,
      [breakpoints.up('xLg')]: {
        fontSize: 10,
        lineHeight: '12px',
      },
      [breakpoints.up('xl')]: {
        fontSize: 12,
        lineHeight: '14px',
      },
    },
    currentDay: {
      backgroundColor: primary.blue6,
    },
    userActionWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    userAvatar: {
      marginRight: spacing(1),
    },
  })
);

const Body = ({
  absenceReports,
  calendar,
  todayPositionIndex,
  canResolveRequestCheck,
  canAccessUserProfileCheck,
  onResolveRequest,
  onGoToUserProfile,
  hasDoubleApprove,
}) => {
  const classes = useStyles({
    todayPositionIndex,
    daysCount: calendar?.intervalDays?.length || 0,
  });

  const handleResolveRequest =
    (user, currentDay, hasPendingRequests, canResolveRequest) => () => {
      if (hasPendingRequests && canResolveRequest) {
        return onResolveRequest(user, currentDay);
      }
    };

  return (
    <div className={classes.root}>
      {absenceReports.map(({ user, intervalDays }) => {
        const isUserAccessible = canAccessUserProfileCheck(user.id);
        const canResolveRequest = canResolveRequestCheck(user);

        return (
          <div key={`absence_report_${user.id}`} className={classes.row}>
            <UserAvatar
              className={classes.user}
              labelClass={classes.userLabel}
              user={user}
              clickableCaption={isUserAccessible}
              onClickHandler={onGoToUserProfile(user.id)}
              caption
              small
            />
            <div className={classes.days}>
              {intervalDays.map((intervalDay, index) => (
                <ConditionalTooltip
                  key={`current_day_${intervalDay.day}_${index}`}
                  customLabelClass={classes.holidayTooltip}
                  addTooltip={!!intervalDay.holiday}
                  message={intervalDay?.holiday?.name || ''}
                >
                  <div
                    className={classNames(classes.day, {
                      [classes.approvedAbsence]:
                        intervalDay.hasAbsenceRequest &&
                        intervalDay.isApprovedAbsence &&
                        !hasDoubleApprove,
                      [classes.halfApprovedAbsence]:
                        intervalDay.hasAbsenceRequest &&
                        intervalDay.isHalfApprovedAbsence &&
                        hasDoubleApprove,
                      [classes.pendingAbsence]:
                        intervalDay.hasAbsenceRequest &&
                        !intervalDay.isApprovedAbsence &&
                        !hasDoubleApprove,
                      [classes.pendingAbsenceDoubleApprove]:
                        intervalDay.hasAbsenceRequest &&
                        !intervalDay.isHalfApprovedAbsence &&
                        !intervalDay.isApprovedAbsence &&
                        hasDoubleApprove,
                      [classes.approvedAbsenceDoubleApprove]:
                        intervalDay.hasAbsenceRequest &&
                        intervalDay.isApprovedAbsence &&
                        hasDoubleApprove,
                      [classes.weekend]: intervalDay.isWeekend,
                      [classes.holiday]: !!intervalDay.holiday,
                      [classes.currentDay]:
                        index === todayPositionIndex &&
                        !intervalDay.holiday &&
                        !intervalDay.hasAbsenceRequest,
                      [classes.pendingAbsenceActive]:
                        canResolveRequest &&
                        intervalDay.hasAbsenceRequest &&
                        !intervalDay.isApprovedAbsence,
                    })}
                    onClick={handleResolveRequest(
                      user,
                      intervalDay.date,
                      intervalDay.hasAbsenceRequest &&
                        !intervalDay.isApprovedAbsence,
                      canResolveRequest
                    )}
                  >
                    <Typography className={classes.dayLabel} variant="overline">
                      {intervalDay.holiday || intervalDay.hasAbsenceRequest
                        ? intervalDay.day
                        : ''}
                    </Typography>
                  </div>
                </ConditionalTooltip>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

Body.propTypes = {
  absenceReports: PropTypes.arrayOf(PropTypes.object).isRequired,
  calendar: PropTypes.object.isRequired,
  todayPositionIndex: PropTypes.number.isRequired,
  hasDoubleApprove: PropTypes.bool.isRequired,
  onResolveRequest: PropTypes.func.isRequired,
  onGoToUserProfile: PropTypes.func.isRequired,
  canAccessUserProfileCheck: PropTypes.func.isRequired,
  canResolveRequestCheck: PropTypes.func.isRequired,
};

export default Body;
